import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ImportStatusIndicator } from '@/components/import/import-status-indicator';
import {
  LayoutDashboard,
  Package,
  ArrowRightLeft,
  Search,
  Settings,
  HelpCircle,
  Menu,
  X,
  ChevronDown,
  PlusCircle,
  TrendingDown,
  RefreshCw,
  Sparkles,
  MapPin,
  FolderTree,
  Wrench,
  Trash2,
  Upload,
  FileText,
  Sliders,
  BarChart3,
  Shield,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { GlobalSearch } from './global-search';
import { Breadcrumb } from './breadcrumb';
import { CopilotLauncher } from '@/components/copilot/copilot-launcher';
import { CopilotPanel } from '@/components/copilot/copilot-panel';
import { useCopilot } from '@/hooks/use-copilot';
import { useCopilotContext } from '@/providers/copilot-provider';
import { useApplyFeatureModeForRoute } from '@/hooks/use-feature-mode';

interface NavItem {
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
}

const navigationItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Assets',
    href: '/assets',
    icon: Package,
  },
  {
    title: 'Transactions',
    icon: ArrowRightLeft,
    children: [
      {
        title: 'Additions',
        href: '/transactions/additions',
        icon: PlusCircle,
      },
      {
        title: 'Depreciation',
        href: '/transactions/depreciation',
        icon: TrendingDown,
      },
      {
        title: 'Revaluation',
        href: '/transactions/revaluation',
        icon: RefreshCw,
      },
    ],
  },
  {
    title: 'Management',
    icon: Shield,
    children: [
      {
        title: 'Check-In / Check-Out',
        href: '/checkinout',
        icon: Package,
      },
      {
        title: 'Compliance',
        href: '/compliance',
        icon: Shield,
      },
    ],
  },
  {
    title: 'Reports',
    icon: BarChart3,
    children: [
      {
        title: 'Insights',
        href: '/insights',
        icon: Sparkles,
      },
      {
        title: 'Asset History',
        href: '/enquiry',
        icon: Search,
      },
      {
        title: 'Audit Trail',
        href: '/audit',
        icon: FileText,
      },
    ],
  },
  {
    title: 'Data Management',
    icon: Upload,
    children: [
      {
        title: 'Import Assets',
        href: '/administration/imports',
        icon: Upload,
      },
      {
        title: 'Import Transactions',
        href: '/administration/import-transactions',
        icon: Upload,
      },
    ],
  },
  {
    title: 'Configuration',
    icon: Sliders,
    children: [
      {
        title: 'Locations',
        href: '/administration/locations',
        icon: MapPin,
      },
      {
        title: 'Categories',
        href: '/administration/categories',
        icon: FolderTree,
      },
      {
        title: 'Equipment',
        href: '/administration/equipment',
        icon: Wrench,
      },
      {
        title: 'Disposal Reasons',
        href: '/administration/disposal-reasons',
        icon: Trash2,
      },
    ],
  },
  {
    title: 'Settings',
    icon: Settings,
    children: [
      {
        title: 'Feature Settings',
        href: '/settings/features',
        icon: Sliders,
      },
      {
        title: 'Asset Options',
        href: '/settings/asset-options',
        icon: Settings,
      },
    ],
  },
  {
    title: 'Help & Support',
    href: '/help',
    icon: HelpCircle,
  },
];

export function AppLayout(): React.ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const location = useLocation();
  const { isOpen, open, close } = useCopilot();
  const { contextChips } = useCopilotContext();
  
  // Apply feature-specific mode based on current route
  // This syncs the module's configured mode from Feature Settings to ModeProvider
  useApplyFeatureModeForRoute();

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Copilot Panel */}
      <CopilotPanel
        isOpen={isOpen}
        onClose={close}
        onMinimize={close}
        contextChips={contextChips}
      />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 transform border-r bg-card transition-all duration-300 ease-in-out lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b px-4">
            <button
              onClick={toggleSidebarCollapse}
              className="flex items-center gap-2 overflow-hidden hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none p-0"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Package className="h-6 w-6 text-primary flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="text-lg font-semibold whitespace-nowrap">Asset Manager</span>
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden flex-shrink-0"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-2 py-4">
            <ScrollAreaViewport className="h-full">
              <nav className="space-y-1">
                {navigationItems.map((item) => (
                  <NavItemComponent
                    key={item.title}
                    item={item}
                    currentPath={location.pathname}
                    collapsed={sidebarCollapsed}
                    onNavigate={() => setSidebarOpen(false)}
                  />
                ))}
              </nav>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* User section */}
          <div className="border-t p-3">
            <div className={cn(
              "flex items-center gap-3",
              sidebarCollapsed ? "flex-col" : "justify-between"
            )}>
              <div className={cn(
                "flex items-center gap-3",
                sidebarCollapsed && "justify-center"
              )}>
                <Avatar className="h-9 w-9 flex-shrink-0">
                  <AvatarImage 
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=faces" 
                    alt="Jasmine Williams" 
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    <span className="text-sm font-medium">JW</span>
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed && (
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-medium">Jasmine Williams</p>
                    <p className="truncate text-xs text-muted-foreground">
                      Dynamic Racing LTD
                    </p>
                  </div>
                )}
              </div>
              <Link to="/settings/asset-options" onClick={() => setSidebarOpen(false)}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-9 w-9 flex-shrink-0",
                    location.pathname === '/settings/asset-options' && "bg-accent text-accent-foreground"
                  )}
                  title="Settings"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center gap-4 border-b bg-card px-6">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden flex-shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <Breadcrumb />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-full max-w-[45rem] hidden md:block">
              <GlobalSearch />
            </div>
            <ThemeToggle />
            <CopilotLauncher onClick={open} isOpen={isOpen} />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Import Status Indicator */}
      <ImportStatusIndicator />
    </div>
  );
}

function NavItemComponent({
  item,
  currentPath,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  currentPath: string;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(
    item.children?.some((child) => child.href && currentPath.startsWith(child.href)) || false
  );
  const isActive = item.href && currentPath === item.href;
  const Icon = item.icon;

  const handleNavClick = (href: string | undefined, e: React.MouseEvent) => {
    if (!href) return;
    // If clicking on the same page, force a refresh by navigating with replace
    if (currentPath === href) {
      e.preventDefault();
      navigate(href, { replace: true });
      // Force a page reload by navigating away and back
      navigate(href + '?refresh=' + Date.now(), { replace: true });
      setTimeout(() => {
        navigate(href, { replace: true });
      }, 0);
    }
    onNavigate();
  };

  // Collapsed state - show only icon
  if (collapsed) {
    if (item.children) {
      // For parent items with children, show icon with tooltip
      const hasActiveChild = item.children.some((child) => child.href && currentPath === child.href);
      return (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'w-full h-10',
            hasActiveChild && 'bg-accent text-accent-foreground'
          )}
          title={item.title}
        >
          <Icon className="h-5 w-5" />
        </Button>
      );
    }
    
    // For regular items, show as link
    if (item.href) {
      return (
        <Link to={item.href} onClick={(e) => handleNavClick(item.href, e)}>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'w-full h-10',
              isActive && 'bg-accent text-accent-foreground'
            )}
            title={item.title}
          >
            <Icon className="h-5 w-5" />
          </Button>
        </Link>
      );
    }
  }

  // Expanded state - show full menu
  if (item.children) {
    return (
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start gap-3 font-normal h-10',
              item.children.some((child) => child.href && currentPath === child.href) &&
                'bg-accent text-accent-foreground'
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1 text-left">{item.title}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                open && 'rotate-180'
              )}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="ml-4 mt-1 space-y-1 border-l pl-4">
          {item.children.map((child) => {
            if (!child.href) return null;
            const ChildIcon = child.icon;
            const isChildActive = currentPath === child.href;
            return (
              <Link 
                key={child.href} 
                to={child.href} 
                onClick={(e) => handleNavClick(child.href, e)}
              >
                <Button
                  variant="ghost"
                  className={cn(
                    'w-full justify-start gap-3 font-normal h-9',
                    isChildActive && 'bg-accent text-accent-foreground'
                  )}
                >
                  <ChildIcon className="h-4 w-4" />
                  {child.title}
                </Button>
              </Link>
            );
          })}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  if (item.href) {
    return (
      <Link to={item.href} onClick={(e) => handleNavClick(item.href, e)}>
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-start gap-3 font-normal h-10',
            isActive && 'bg-accent text-accent-foreground'
          )}
        >
          <Icon className="h-4 w-4" />
          {item.title}
        </Button>
      </Link>
    );
  }

  return null;
}

function getPageInfo(pathname: string): { title: string; icon: React.ComponentType<{ className?: string }> } {
  const pages: Record<string, { title: string; icon: React.ComponentType<{ className?: string }> }> = {
    '/': { title: 'Dashboard', icon: LayoutDashboard },
    '/dashboard': { title: 'Dashboard', icon: LayoutDashboard },
    '/assets': { title: 'Assets', icon: Package },
    '/transactions/additions': { title: 'Asset Additions', icon: PlusCircle },
    '/transactions/depreciation': { title: 'Asset Depreciation', icon: TrendingDown },
    '/transactions/revaluation': { title: 'Asset Revaluation', icon: RefreshCw },
    '/insights': { title: 'Insights', icon: Sparkles },
    '/enquiry': { title: 'Asset History', icon: Search },
    '/audit': { title: 'Audit Trail', icon: FileText },
    '/checkinout': { title: 'Check-In / Check-Out', icon: Package },
    '/checkinout/calendar': { title: 'Calendar View', icon: FileText },
    '/checkinout/settings': { title: 'Check-In / Check-Out Settings', icon: Settings },
    '/compliance': { title: 'Compliance', icon: Shield },
    '/compliance/settings': { title: 'Compliance Settings', icon: Settings },
    '/administration': { title: 'Administration', icon: Settings },
    '/administration/imports': { title: 'Import Assets', icon: Upload },
    '/administration/import-transactions': { title: 'Import Transactions', icon: ArrowRightLeft },
    '/administration/locations': { title: 'Locations', icon: MapPin },
    '/administration/categories': { title: 'Asset Categories', icon: FolderTree },
    '/administration/equipment': { title: 'Equipment', icon: Wrench },
    '/administration/disposal-reasons': { title: 'Disposal Reasons', icon: Trash2 },
    '/settings/features': { title: 'Feature Settings', icon: Sliders },
    '/settings/asset-options': { title: 'Asset Options', icon: Settings },
    '/help': { title: 'Help & Support', icon: HelpCircle },
  };
  return pages[pathname] || { title: 'Asset Management', icon: Package };
}

function getPageTitle(pathname: string): string {
  return getPageInfo(pathname).title;
}
